/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import BigNumber from 'bignumber.js'
import vaultsConfig from 'config/constants/vaults'
import { getAddress } from 'utils/addressHelpers'
import fetchVaults from './fetchVaults'
import { fetchVaultUserAllowances, fetchVaultUserTokenBalances, fetchVaultUserSharesBalances } from './fetchVaultUser'
import { VaultsState, Vault } from '../types'

const noAccountVaultConfig = vaultsConfig.map((vault) => ({
  ...vault,
  userData: {
    allowance: '0',
    tokenBalance: '0',
    sharesBalance: '0',
  },
}))

const initialState: VaultsState = { data: noAccountVaultConfig, userDataLoaded: false }

// Async thunks
export const fetchVaultsPublicDataAsync = (vids) => async (dispatch, getState) => {
  const pricePerfullSharesHistory = getState().apiServices.pricePerfullSharesHistory.data
  const prices = getState().farms.prices

  const vaultsToFetch = vaultsConfig.filter((vaultConfig) => vids.includes(vaultConfig.vid))
  const vaults = await fetchVaults(vaultsToFetch)

  const vaultsData = vaults.map((vault) => {
    const filteredPpfsHistory = pricePerfullSharesHistory.filter(
      (item) => item.values[getAddress(vault.contractAddress)],
    )
    let pastPpfs = null
    let daysAgo = 0
    if (filteredPpfsHistory.length) {
      pastPpfs = filteredPpfsHistory[0]
      const currentTimestamp = +new Date()
      daysAgo = (currentTimestamp - parseInt(pastPpfs.timestamp)) / 1000 / 60 / 60 / 24
    }
    const roiDay =
      pastPpfs && daysAgo
        ? vault.pricePerFullShare?.div(new BigNumber(pastPpfs.values[getAddress(vault.contractAddress)])).toNumber() **
            (1 / daysAgo) -
          1
        : 0

    const tokenAddress = vault.token.address ? getAddress(vault.token.address).toLowerCase() : null
    const tokenPrice = tokenAddress ? prices[tokenAddress] : 0
    const tvl = vault.tokenAmountTotal?.multipliedBy(tokenPrice)

    return {
      ...vault,
      apy: ((roiDay + 1) ** 365 - 1) * 100,
      daily: roiDay * 100,
      tokenPrice,
      tvl,
    }
  })
  dispatch(setVaultsPublicData(vaultsData))
}

interface VaultUserDataResponse {
  vid: number
  allowance: string
  tokenBalance: string
  sharesBalance: string
}

export const fetchVaultUserDataAsync = createAsyncThunk<VaultUserDataResponse[], { account: string; vids: number[] }>(
  'vaults/fetchVaultUserDataAsync',
  async ({ account, vids }) => {
    const vaultsToFetch = vaultsConfig.filter((vaultConfig) => vids.includes(vaultConfig.vid))
    const userVaultAllowances = await fetchVaultUserAllowances(account, vaultsToFetch)
    const userVaultTokenBalances = await fetchVaultUserTokenBalances(account, vaultsToFetch)

    const userSharesBalances = await fetchVaultUserSharesBalances(account, vaultsToFetch)
    return userVaultAllowances.map((vaultAllowance, index) => {
      if (index === 0) {
        return {
          vid: vaultsToFetch[index].vid,
          allowance: userVaultAllowances[index],
          tokenBalance: userVaultTokenBalances[index],
          sharesBalance: userSharesBalances[vaultsToFetch[index].vid].split('/')[0],
          lastDepositedTime: userSharesBalances[vaultsToFetch[index].vid].split('/')[1],
        }
      }
      return {
        vid: vaultsToFetch[index].vid,
        allowance: userVaultAllowances[index],
        tokenBalance: userVaultTokenBalances[index],
        sharesBalance: userSharesBalances[vaultsToFetch[index].vid],
      }
    })
  },
)

export const updateUserVaultsAllowance =
  (vid: number, account: string): any =>
  async (dispatch) => {
    const vaultsToFetch = vaultsConfig
    const allowances = await fetchVaultUserAllowances(account, vaultsToFetch)
    dispatch(updateVaultsUserData({ vid, field: 'allowance', value: allowances[vid] }))
  }

export const updateUserVaultsBalance =
  (vid: number, account: string): any =>
  async (dispatch) => {
    const vaultsToFetch = vaultsConfig
    const balances = await fetchVaultUserTokenBalances(account, vaultsToFetch)
    dispatch(updateVaultsUserData({ vid, field: 'tokenBalance', value: balances[vid] }))
  }

export const updateUserVaultsDepositBalance =
  (vid: number, account: string): any =>
  async (dispatch) => {
    const vaultsToFetch = vaultsConfig
    const sharesBalances = await fetchVaultUserSharesBalances(account, vaultsToFetch)
    dispatch(updateVaultsUserData({ vid, field: 'sharesBalance', value: sharesBalances[vid] }))
  }

export const vaultsSlice = createSlice({
  name: 'Vaults',
  initialState,
  reducers: {
    setVaultsPublicData: (state, action) => {
      const liveVaultsData: Vault[] = action.payload
      state.data = state.data.map((vault) => {
        const liveVaultData = liveVaultsData.find((vaultData) => vaultData.vid === vault.vid)
        return { ...vault, ...liveVaultData }
      })
    },
    updateVaultsUserData: (state, action) => {
      const { field, value, vid } = action.payload
      const index = state.data.findIndex((p) => p.vid === vid)

      if (field === 'sharesBalance' && index === 0) {
        state.data[index] = {
          ...state.data[index],
          userData: {
            ...state.data[index].userData,
            sharesBalance: value.split('/')[0],
            lastDepositedTime: value.split('/')[1],
          },
        }
      } else if (index >= 0) {
        state.data[index] = { ...state.data[index], userData: { ...state.data[index].userData, [field]: value } }
      }
    },
  },
  extraReducers: (builder) => {
    // Update vaults with user data
    builder.addCase(fetchVaultUserDataAsync.fulfilled, (state, action) => {
      action.payload.forEach((userDataEl) => {
        const { vid } = userDataEl
        const index = state.data.findIndex((vault) => vault.vid === vid)
        state.data[index] = { ...state.data[index], userData: userDataEl }
      })
      state.userDataLoaded = true
    })
  },
})

export const { setVaultsPublicData, updateVaultsUserData } = vaultsSlice.actions

export default vaultsSlice.reducer
